/**
 * useWorkstreams / useWorkstream — the React bindings for the workstreams
 * feature.
 *
 * Strict layering: pages call THESE hooks; the hooks call {@link WorkstreamStore}
 * (and, for the detail view's cross-entity resolution, the existing
 * {@link GoalStore} / {@link TaskStore}); the stores call the api facade. Pages
 * never import a store or the api directly.
 *
 * - {@link useWorkstreams} owns the board LIST (summaries) + create/remove/
 *   follow, exposes the current user's alias (for the "New" form owner default
 *   and follow-state), and the AI-STRUCTURE assist.
 * - {@link useWorkstream} owns ONE workstream's detail: it hydrates the full
 *   document and resolves the linked goal + sprint stories CLIENT-SIDE (title
 *   lookups over the goals/tasks pointer rows), and exposes save / follow /
 *   setStage.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { GoalStore } from '../goals/goalStore';
import { TaskStore } from '../tasks/taskStore';
import { WorkstreamStore } from './workstreamStore';
import {
  structureWorkstreamFromNotes,
  type StructuredWorkstreamFields,
} from './aiStructure';
import { sortByRecency } from './workstreamModel';
import type { Workstream, WorkstreamDraft, WorkstreamSummary } from './types';
import type { GoalSummary } from '../goals/types';
import type { Task } from '../tasks/types';

// ---------------------------------------------------------------------------
// useWorkstreams — board list + mutations + AI structuring
// ---------------------------------------------------------------------------

export interface UseWorkstreamsResult {
  /** All non-deleted workstream summaries (most-recently-updated first). */
  workstreams: WorkstreamSummary[];
  /** Goal summaries (to resolve linked goal titles + populate the goal picker). */
  goals: GoalSummary[];
  /** Current user's alias (owner default + follow-state). */
  currentAlias: string;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  create: (draft: WorkstreamDraft) => Promise<Workstream>;
  remove: (id: string) => Promise<void>;
  /** Toggles the current user in a workstream's followers. */
  toggleFollow: (id: string) => Promise<void>;
  /** AI-STRUCTURE: structures free-form notes into partial draft fields. */
  structureFromNotes: (notes: string) => Promise<StructuredWorkstreamFields>;
}

export function useWorkstreams(): UseWorkstreamsResult {
  const api = useApi();
  const stores = useMemo(
    () => ({ workstreams: new WorkstreamStore(api), goals: new GoalStore(api) }),
    [api],
  );

  const [workstreams, setWorkstreams] = useState<WorkstreamSummary[]>([]);
  const [goals, setGoals] = useState<GoalSummary[]>([]);
  const [currentAlias, setCurrentAlias] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rows, goalRows, identity] = await Promise.all([
        stores.workstreams.listSummaries(),
        stores.goals.listSummaries(),
        api.identityProvider.getIdentity(),
      ]);
      setWorkstreams(sortByRecency(rows));
      setGoals(goalRows);
      setCurrentAlias(identity.alias);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load workstreams.');
    } finally {
      setLoading(false);
    }
  }, [stores, api]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const create = useCallback(
    async (draft: WorkstreamDraft) => {
      const saved = await stores.workstreams.create(draft);
      await refresh();
      return saved;
    },
    [stores, refresh],
  );

  const remove = useCallback(
    async (id: string) => {
      await stores.workstreams.softDelete(id);
      await refresh();
    },
    [stores, refresh],
  );

  const toggleFollow = useCallback(
    async (id: string) => {
      await stores.workstreams.toggleFollow(id);
      await refresh();
    },
    [stores, refresh],
  );

  const structureFromNotes = useCallback(
    (notes: string) => structureWorkstreamFromNotes(api.inference, notes),
    [api],
  );

  return {
    workstreams,
    goals,
    currentAlias,
    loading,
    error,
    refresh,
    create,
    remove,
    toggleFollow,
    structureFromNotes,
  };
}

// ---------------------------------------------------------------------------
// useWorkstream — single-workstream detail with client-side link resolution
// ---------------------------------------------------------------------------

/** A resolved linked story (task id + best-known title). */
export interface LinkedStory {
  id: string;
  title: string;
  /** True when the id resolved to a real task. */
  resolved: boolean;
}

export interface UseWorkstreamResult {
  workstream: Workstream | null;
  /** Resolved linked goal summary, if any. */
  goal: GoalSummary | null;
  /** All goal summaries (for the edit modal's goal picker). */
  allGoals: GoalSummary[];
  /** Resolved linked sprint stories. */
  stories: LinkedStory[];
  /** Whether the current user follows this workstream. */
  currentAlias: string;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  save: (draft: WorkstreamDraft) => Promise<Workstream>;
  toggleFollow: () => Promise<void>;
  setStage: (stage: string) => Promise<Workstream>;
  /** AI-STRUCTURE: structures free-form notes into partial draft fields. */
  structureFromNotes: (notes: string) => Promise<StructuredWorkstreamFields>;
}

export function useWorkstream(id: string | undefined): UseWorkstreamResult {
  const api = useApi();
  const stores = useMemo(
    () => ({
      workstreams: new WorkstreamStore(api),
      goals: new GoalStore(api),
      tasks: new TaskStore(api),
    }),
    [api],
  );

  const [workstream, setWorkstream] = useState<Workstream | null>(null);
  const [allGoals, setAllGoals] = useState<GoalSummary[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [currentAlias, setCurrentAlias] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!id) {
      setWorkstream(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [loaded, goalRows, allTasks, identity] = await Promise.all([
        stores.workstreams.get(id),
        stores.goals.listSummaries(),
        stores.tasks.listAll(),
        api.identityProvider.getIdentity(),
      ]);
      setAllGoals(goalRows);
      setTasks(allTasks);
      setCurrentAlias(identity.alias);
      if (!loaded || loaded.deleted) {
        setWorkstream(null);
        setError('Workstream not found.');
        return;
      }
      setWorkstream(loaded);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load workstream.');
    } finally {
      setLoading(false);
    }
  }, [id, stores, api]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const goal = useMemo(() => {
    if (!workstream?.goalId) return null;
    return allGoals.find((g) => g.id === workstream.goalId) ?? null;
  }, [workstream, allGoals]);

  const stories = useMemo<LinkedStory[]>(() => {
    if (!workstream) return [];
    const byId = new Map(tasks.map((t) => [t.id, t]));
    return (workstream.sprintStoryIds ?? []).map((sid) => {
      const t = byId.get(sid);
      return { id: sid, title: t?.title ?? sid, resolved: !!t };
    });
  }, [workstream, tasks]);

  const save = useCallback(
    async (draft: WorkstreamDraft) => {
      if (!workstream) throw new Error('No workstream loaded to save.');
      const saved = await stores.workstreams.applyDraft(workstream, draft);
      await refresh();
      return saved;
    },
    [workstream, stores, refresh],
  );

  const toggleFollow = useCallback(async () => {
    if (!workstream) return;
    await stores.workstreams.toggleFollow(workstream.id);
    await refresh();
  }, [workstream, stores, refresh]);

  const setStage = useCallback(
    async (stage: string) => {
      if (!workstream) throw new Error('No workstream loaded to update.');
      const saved = await stores.workstreams.setStage(workstream, stage);
      await refresh();
      return saved;
    },
    [workstream, stores, refresh],
  );

  const structureFromNotes = useCallback(
    (notes: string) => structureWorkstreamFromNotes(api.inference, notes),
    [api],
  );

  return {
    workstream,
    goal,
    allGoals,
    stories,
    currentAlias,
    loading,
    error,
    refresh,
    save,
    toggleFollow,
    setStage,
    structureFromNotes,
  };
}
